import React, { useState, useRef, useEffect } from "react";
import { FiFilter, FiList, FiGrid } from "react-icons/fi";
import { BsChevronRight } from "react-icons/bs";
import { getPriorities, getCategories } from "../../../../api/masterOptions";
import { getActiveEmployees } from "../../../../api/ticketing";
const TIMES = ["Any Time", "Last 7 Days", "Last 15 Days", "Last 30 Days"];
const STATUS_COLORS = {
  "New": "#3b82f6",
  "In Progress": "#f97316",
  "Reopen": "#eab308",
  "Resolved": "#a855f7",
  "Rejected": "#ef4444",
  "Closed": "#22c55e",
};

// ── Reusable custom dropdown ──────────────────────────────────────────────────
const CustomDropdown = ({ options, value, onChange }) => {
  const [open, setOpen] = useState(false);
  const ref = useRef(null);

  useEffect(() => {
    const handler = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, []);

  return (
    <div ref={ref} style={{ position: "relative" }}>
      {/* Trigger button */}
      <button
        onClick={() => setOpen(o => !o)}
        style={{
          display: "flex",
          alignItems: "center",
          gap: 24,
          height: 34,
          padding: "0 12px",
          background: "#fff",
          border: "1px solid #e5e7eb",
          borderRadius: 7,
          fontSize: 13,
          fontWeight: 400,
          color: "#374151",
          cursor: "pointer",
          fontFamily: "Inter, -apple-system, BlinkMacSystemFont, sans-serif",
          whiteSpace: "nowrap",
          minWidth: 130,
          justifyContent: "space-between",
        }}
      >
        <span>{value}</span>
        <BsChevronRight
          size={11}
          color="#9ca3af"
          style={{ flexShrink: 0 }}
        />
      </button>

      {/* Dropdown menu */}
      {open && (
        <div style={{
          position: "absolute",
          top: "calc(100% + 4px)",
          left: 0,
          minWidth: "100%",
          background: "#fff",
          border: "1px solid #e5e7eb",
          borderRadius: 8,
          boxShadow: "0 4px 16px rgba(0,0,0,0.10)",
          zIndex: 100,
          overflow: "hidden",
        }}>
          {options.map(opt => (
            <div
              key={opt}
              onClick={() => { onChange(opt); setOpen(false); }}
              style={{
                padding: "8px 14px",
                fontSize: 13,
                color: opt === value ? "#2563eb" : "#374151",
                background: opt === value ? "#eff6ff" : "transparent",
                cursor: "pointer",
                fontFamily: "Inter, -apple-system, BlinkMacSystemFont, sans-serif",
                fontWeight: opt === value ? 500 : 400,
                transition: "background 0.1s",
              }}
              onMouseEnter={e => { if (opt !== value) e.currentTarget.style.background = "#f9fafb"; }}
              onMouseLeave={e => { if (opt !== value) e.currentTarget.style.background = "transparent"; }}
            >
              {opt}
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

// ── Status dropdown with colored dots ────────────────────────────────────────
const StatusDropdown = ({ value, onChange, statuses }) => {
  const [open, setOpen] = useState(false);
  const ref = useRef(null);

  useEffect(() => {
    const handler = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, []);

  const selected = statuses.find(s => s.label === value) || statuses[0];

  return (
    <div ref={ref} style={{ position: "relative" }}>
      <button
        onClick={() => setOpen(o => !o)}
        style={{
          display: "flex", alignItems: "center", gap: 24,
          height: 34, padding: "0 12px",
          background: "#fff", border: "1px solid #e5e7eb", borderRadius: 7,
          fontSize: 13, fontWeight: 400, color: "#374151",
          cursor: "pointer", fontFamily: "Inter, -apple-system, BlinkMacSystemFont, sans-serif",
          whiteSpace: "nowrap", minWidth: 130, justifyContent: "space-between",
        }}
      >
        <span>{selected.label}</span>
        <BsChevronRight size={11} color="#9ca3af" style={{ flexShrink: 0 }} />
      </button>

      {open && (
        <div style={{
          position: "absolute", top: "calc(100% + 4px)", left: 0, minWidth: "100%",
          background: "#fff", border: "1px solid #e5e7eb", borderRadius: 8,
          boxShadow: "0 4px 16px rgba(0,0,0,0.10)", zIndex: 100, overflow: "hidden",
        }}>
          {statuses.map(s => (
            <div
              key={s.label}
              onClick={() => { onChange(s.label); setOpen(false); }}
              style={{
                padding: "8px 14px", fontSize: 13,
                color: s.label === value ? "#2563eb" : "#374151",
                background: s.label === value ? "#eff6ff" : "transparent",
                cursor: "pointer",
                fontFamily: "Inter, -apple-system, BlinkMacSystemFont, sans-serif",
                fontWeight: s.label === value ? 500 : 400,
                transition: "background 0.1s",
              }}
              onMouseEnter={e => { if (s.label !== value) e.currentTarget.style.background = "#f9fafb"; }}
              onMouseLeave={e => { if (s.label !== value) e.currentTarget.style.background = "transparent"; }}
            >
              {s.label}
            </div>
          ))}
        </div>
      )}
    </div>
  );
};


const TicketFilters = ({ filters, onChange, viewMode = "list", onViewModeChange, totalCount = 0, filteredCount = 0, tickets = [] }) => {
  const sel = (field, value) => onChange({ ...filters, [field]: value });

  const token = localStorage.getItem("token");
  const [priorityOptions, setPriorityOptions] = useState(["All Priority"]);
  const [typeOptions, setTypeOptions] = useState(["All Types"]);
  const [allowedResources, setAllowedResources] = useState(null); // null = not loaded yet

  useEffect(() => {
    getPriorities(token)
      .then(res => setPriorityOptions(["All Priority", ...(res.data || []).map(p => p.priority)]))
      .catch(() => { });
    getCategories(token)
      .then(res => setTypeOptions(["All Types", ...(res.data || []).map(c => c.category)]))
      .catch(() => { });
    getActiveEmployees(token)
      .then(res => setAllowedResources(new Set((res.data || []).map(e => e.name))))
      .catch(() => setAllowedResources(new Set()));
  }, [token]);

  const presentStatuses = Object.keys(STATUS_COLORS).filter(
    label => label !== "Closed" && tickets.some(t => t.status === label)
  );
  const statusOptions = [
    { label: "All Status", color: null },
    ...presentStatuses.map(label => ({ label, color: STATUS_COLORS[label] })),
  ];

  const presentResources = Array.from(
    new Set(
      tickets
        .map(t => t.assignee)
        .filter(name => name && allowedResources && allowedResources.has(name))
    )
  ).sort();
  const resourceOptions = ["All Resource", ...presentResources];

  const [moreOpen, setMoreOpen] = useState(false);
  const moreRef = useRef(null);

  useEffect(() => {
    const handler = (e) => { if (moreRef.current && !moreRef.current.contains(e.target)) setMoreOpen(false); };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, []);

  return (
    <div style={{
      display: "flex", alignItems: "center", justifyContent: "space-between",
      gap: 10, padding: "10px 0", width: "100%",
    }}>

      {/* Left side: filter dropdowns */}
      <div style={{ display: "flex", alignItems: "center", gap: 8 }}>

        {/* Status filter — FIRST, before All Priority */}
        <StatusDropdown
          value={filters.status || "All Status"}
          onChange={v => sel("status", v)}
          statuses={statusOptions}
        />

        <CustomDropdown
          options={priorityOptions}

          value={filters.priority || "All Priority"}
          onChange={v => sel("priority", v)}
        />

        <CustomDropdown
          options={typeOptions}
          value={filters.type || "All Types"}
          onChange={v => sel("type", v)}
        />

        <CustomDropdown
          options={TIMES}
          value={filters.time || "Any Time"}
          onChange={v => sel("time", v)}
        />

        {/* More button */}

        {/* More button */}
        <div ref={moreRef} style={{ position: "relative" }}>
          <button
            onClick={() => setMoreOpen(o => !o)}
            style={{
              display: "flex",
              alignItems: "center",
              gap: 5,
              height: 34,
              padding: "0 12px",
              background: "transparent",
              border: "none",
              fontSize: 13,
              color: "#374151",
              cursor: "pointer",
              fontFamily: "Inter, -apple-system, BlinkMacSystemFont, sans-serif",
              fontWeight: 400,
            }}
          >
            <FiFilter size={14} color="#6b7280" />
            <span>More</span>
          </button>

          {moreOpen && (
            <div style={{
              position: "absolute",
              top: "calc(100% + 4px)",
              left: 0,
              minWidth: 220,
              background: "#fff",
              border: "1px solid #e5e7eb",
              borderRadius: 8,
              boxShadow: "0 4px 16px rgba(0,0,0,0.10)",
              zIndex: 100,
              padding: 12,
            }}>
              <div style={{ fontSize: 11, fontWeight: 600, color: "#9ca3af", textTransform: "uppercase", letterSpacing: "0.06em", marginBottom: 6 }}>
                Resource
              </div>
              <CustomDropdown
                options={resourceOptions}
                value={filters.resource || "All Resource"}
                onChange={v => sel("resource", v)}
              />
            </div>
          )}
        </div>
      </div>

      <div style={{ display: "flex", alignItems: "center", gap: 12, marginLeft: "auto" }}>

        {/* List / Grid toggle */}
        <div style={{
          display: "flex",
          alignItems: "center",
          border: "1px solid #e5e7eb",
          borderRadius: 7,
          overflow: "hidden",
          background: "#fff",
        }}>
          <button
            onClick={() => onViewModeChange?.("list")}
            title="List view"
            style={{
              display: "flex", alignItems: "center", justifyContent: "center",
              width: 34, height: 34,
              background: viewMode === "list" ? "#f3f4f6" : "#fff",
              border: "none",
              borderRight: "1px solid #e5e7eb",
              cursor: "pointer",
              color: viewMode === "list" ? "#374151" : "#9ca3af",
            }}
          >
            <FiList size={15} />
          </button>
          <button
            onClick={() => onViewModeChange?.("grid")}
            title="Grid view"
            style={{
              display: "flex", alignItems: "center", justifyContent: "center",
              width: 34, height: 34,
              background: viewMode === "grid" ? "#f3f4f6" : "#fff",
              border: "none",
              cursor: "pointer",
              color: viewMode === "grid" ? "#374151" : "#9ca3af",
            }}
          >
            <FiGrid size={15} />
          </button>
        </div>

      </div>

    </div>
  );
};

export default TicketFilters;